<script setup>
  import ComponentTableRowCheckbox from "@components/Table/TableRowCheckbox.vue";
  import {router} from "@inertiajs/vue3";
  import PatientsPatientInformation from "@pages/Patients/Components/PatientInformation.vue";
  import {MedBadge, MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsExternalPatientRow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    selectedRows: {
      type: Array,
      required: true,
    },
  });

  const emit = defineEmits(["open:patientNote", "open:patientTodos", "confirm:delete", "update:selectedRows"]);

  function openPatientNote() {
    emit("open:patientNote");
  }

  function openPatientTodos() {
    emit("open:patientTodos");
  }

  function confirmDelete() {
    emit("confirm:delete");
  }

  function clickRow(event, href) {
    if (props.patient.revoked) {
      // disable click when revoked
      return;
    }

    let node = event.target ?? event;

    if (!node.nodeName) {
      return;
    }

    if (node.nodeName.toLowerCase() === "td") {
      // click on <td> => open link
      router.visit(href);
      return;
    }

    if (
      node.nodeName.toLowerCase() === "a" ||
      node.nodeName.toLowerCase() === "button" ||
      node.nodeName.toLowerCase() === "select" ||
      node.nodeName.toLowerCase() === "input" ||
      node.nodeName.toLowerCase() === "textarea" ||
      node.nodeName.toLowerCase() === "dialog"
    ) {
      // click on a clickable element (a or button)
      return;
    }

    if (node.parentNode) {
      // click on any other element: check if parent is <td>
      return clickRow(node.parentNode, href);
    }
  }
</script>

<template>
  <tr
    class="border-b-2 border-gray-200 leading-tight"
    :class="{
      'cursor-pointer hover:bg-gray-100': !patient.revoked,
      'cursor-not-allowed text-gray-500': patient.revoked,
      'bg-gray-200': selectedRows.includes(patient.id),
    }"
    :data-test="getTestId('external-row')"
    @click="(event) => clickRow(event, $route('patients.show', {patient: patient.id}))"
  >
    <!-- ☑️ multi-select & patient -->
    <td class="px-0.5 py-2">
      <div class="flex items-center space-x-2">
        <ComponentTableRowCheckbox
          v-if="$page.props?.can?.allow_multi_select"
          :row-key="patient.id"
          :selected-rows="selectedRows"
          :test-id="getTestId('external-row')"
          @update:selected-rows="(newSelectedRows) => $emit('update:selectedRows', newSelectedRows)"
        />

        <MedLink
          as="inertia"
          class="text-lg font-semibold"
          :disabled="patient.revoked"
          :href="$route('patients.show', {patient: patient.id})"
          :data-test="getTestId('external-row-show-patient-link')"
        >
          <MedTooltip content="Patient / Alter">
            <span>{{ patient.lastname }}, {{ patient.firstname }},</span>
            <span data-format="age" :data-encrypted="patient.birthdate" />
          </MedTooltip>
        </MedLink>

        <MedTooltip
          v-if="!patient.revoked"
          :content="patient.note === null ? 'Patientennotiz anlegen' : 'Patientennotiz öffnen'"
        >
          <div class="relative">
            <MedBadge
              variant="secondary"
              :dot="patient.note !== null"
              :data-test="getTestId('external-row-note-badge')"
            >
              <MedButton
                flat
                icon="assignment"
                size="xs"
                rounded="full"
                :aria-label="patient.lastname ? `Notiz für ${patient.lastname} öffnen` : 'Patientennotiz öffnen'"
                :data-test="getTestId('external-row-open-note-button')"
                @click="openPatientNote()"
              />
            </MedBadge>
          </div>
        </MedTooltip>
      </div>

      <div :class="{'ml-8': $page.props?.can?.allow_multi_select}">
        <MedTooltip v-if="patient.birthdate_formatted" content="Geburtsdatum">
          <span class="text-xs">{{ patient.birthdate_formatted }}</span>
        </MedTooltip>

        <MedTooltip v-if="patient.aliasname" content="Alias">
          <span class="text-xs">„{{ patient.aliasname }}“</span>
        </MedTooltip>
      </div>
    </td>

    <!-- herkunft -->
    <td class="px-0.5 py-2">
      <div>{{ patient?.shared_owners[0]?.company }}</div>
      <div class="text-xs">{{ patient?.shared_owners[0]?.name }}</div>
    </td>

    <!-- status -->
    <td class="px-0.5 py-2">
      <PatientsPatientInformation :patient="patient" />
    </td>

    <!-- to dos -->
    <td class="px-0.5 py-2 text-sm">
      <template v-if="!patient.revoked">
        <MedTooltip v-if="patient?.todo_count === 0" content="Neue Aufgabe anlegen">
          <MedButton
            flat
            icon="add"
            size="xs"
            rounded="full"
            :aria-label="patient.lastname ? `Neue Aufgabe für ${patient.lastname} anlegen` : 'Neue Aufgabe anlegen'"
            :data-test="getTestId('external-patient-row-open-todo-button')"
            @click="openPatientTodos"
          />
        </MedTooltip>

        <template v-if="patient?.todo_count > 0">
          <MedLink
            as="button"
            :data-test="getTestId('external-patient-row-open-todo-button')"
            @click.prevent="openPatientTodos()"
          >
            {{ patient?.todo_last?.label }}
          </MedLink>
        </template>

        <div v-if="patient?.todo_count > 1" class="text-xs">
          und

          <MedLink
            as="button"
            variant="primary"
            :data-test="getTestId('external-patient-row-open-todo-button')"
            @click.prevent="openPatientTodos()"
          >
            {{ patient?.todo_count - 1 }} weitere
          </MedLink>
        </div>
      </template>
    </td>

    <!-- last updates -->
    <td class="px-0.5 py-2">
      {{ patient.updated_at }}

      <div v-if="patient.username" class="text-xs">
        {{ patient.username }}
      </div>
    </td>

    <!-- actions -->
    <td class="px-0.5 py-2">
      <div class="flex justify-center">
        <MedTooltip content="Löschen">
          <MedButton
            :aria-label="`Patient ${patient.lastname} löschen`"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('external-patient-row-delete-button')"
            @click="confirmDelete()"
          />
        </MedTooltip>
      </div>
    </td>
  </tr>
</template>
